const KeenSlider = window.KeenSlider

const { setInitialised, useA11y, useArrows, useStrictSlideVisibilityA11y } =
  window.BAO.Carousel.plugins
const { setPreInitSlideA11yState } = window.BAO.Carousel
const pagedViewport = window.matchMedia('(max-width: 1200px)')

const createLargeSlides =
  (perView, spacingPixels) => (containerSize, slides) => {
    const spacing = spacingPixels / containerSize
    const singleSize = 1 / perView - spacing + spacing / perView

    const slideConfigs = slides.map((slide) => {
      const size =
        slide.dataset.cardWidth === 'double'
          ? singleSize * 2 + spacing
          : singleSize

      return { size, spacing }
    })

    const contentSize = slideConfigs.reduce(
      (total, slide, index) => total + slide.size + (index > 0 ? spacing : 0),
      0,
    )
    let checkedSize = 0

    return slideConfigs.map((slide) => {
      const remainingSize = contentSize - checkedSize
      checkedSize += slide.size + spacing

      return {
        ...slide,
        origin:
          remainingSize < 1
            ? 1 - remainingSize - (contentSize > 1 ? 0 : 1 - contentSize)
            : 0,
      }
    })
  }

const getPagedTarget = (slides, currentIndex, direction) => {
  const lastIndex = slides.length - 1

  if (direction === 'next') {
    let targetIndex = Math.min(currentIndex + 1, lastIndex)
    const currentIsDouble =
      slides[currentIndex]?.dataset.cardWidth === 'double'
    const targetIsSingle = slides[targetIndex]?.dataset.cardWidth === 'single'
    const followingIsDouble =
      slides[targetIndex + 1]?.dataset.cardWidth === 'double'

    if (!currentIsDouble && targetIsSingle && followingIsDouble) {
      targetIndex += 1
    }

    return targetIndex
  }

  let targetIndex = Math.max(currentIndex - 1, 0)
  const currentIsDouble = slides[currentIndex]?.dataset.cardWidth === 'double'
  const targetIsSingle = slides[targetIndex]?.dataset.cardWidth === 'single'
  const precedingIsSingle =
    slides[targetIndex - 1]?.dataset.cardWidth === 'single'

  if (currentIsDouble && targetIsSingle && precedingIsSingle) {
    targetIndex -= 1
  }

  return targetIndex
}

const getNavigationTarget = (carousel, direction, usePaging) => {
  const details = carousel.track?.details
  if (!details) return null

  const target = usePaging
    ? getPagedTarget(carousel.slides, details.rel, direction)
    : details.rel + (direction === 'next' ? 1 : -1)

  return Math.min(Math.max(target, details.minIdx), details.maxIdx)
}

const usePagedNavigation = (enabled) => (carousel) => {
  if (!enabled) return

  const defaultNext = carousel.next
  const defaultPrevious = carousel.prev

  carousel.next = () => {
    if (!pagedViewport.matches) {
      defaultNext()
      return
    }

    const target = getNavigationTarget(carousel, 'next', true)
    if (!Number.isInteger(target)) return

    carousel.moveToIdx(target)
  }

  carousel.prev = () => {
    if (!pagedViewport.matches) {
      defaultPrevious()
      return
    }

    const target = getNavigationTarget(carousel, 'previous', true)
    if (!Number.isInteger(target)) return

    carousel.moveToIdx(target)
  }
}

class CtaCarousel extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.carousel = this.setupCarousel()
  }

  setupCarousel () {
    setPreInitSlideA11yState(this.els.slides.element, this.els.slide.selector)

    const isLarge = this.dataset.size === 'large'
    const configuredSlides = Number(this.dataset.slides)

    // Disable desktop carousel if displaying less than the equivalent of 4 single cards
    const desktopUnits = [...this.els.slide.elements].reduce(
      (total, slide) => total + (slide.dataset.cardWidth === 'double' ? 2 : 1),
      0,
    )
    const disableDesktopCarousel = isLarge && desktopUnits <= 4

    const carousel = new KeenSlider(
      this.els.slides.element,
      {
        selector: this.els.slide.selector,
        defaultAnimation: {
          duration: 1500,
        },
        loop: false,
        mode: 'snap',
        slides: isLarge
          ? createLargeSlides(2, 10)
          : {
              perView: configuredSlides - 2.8,
              spacing: 10,
            },
        breakpoints: {
          '(min-width: 768px)': {
            slides: isLarge
              ? createLargeSlides(2.1, 20)
              : {
                  perView: configuredSlides - 2,
                  spacing: 20,
                },
          },
          '(min-width: 1201px)': {
            disabled: disableDesktopCarousel,
            slides: isLarge
              ? createLargeSlides(4, 20)
              : {
                  perView: configuredSlides,
                  spacing: 20,
                },
          },
        },
      },
      [
        usePagedNavigation(isLarge),
        setInitialised(),
        useA11y({
          includeTabindex: false,
        }),
        useStrictSlideVisibilityA11y(0.95),
        useArrows({
          next: this.els?.next?.element,
          previous: this.els?.previous?.element,
        }),
      ],
    )

    this.setupArrowFocusManagement(carousel, isLarge)

    return carousel
  }

  setupArrowFocusManagement (carousel, isLarge) {
    let pendingFocusMove = false
    let pendingTargetSlideIndex = null
    let pendingTabFocusTarget = null
    let pendingTabIntent = false
    let isKeyboardArrowActivation = false
    const focusableSelector =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

    const resolveTargetFocusable = () => {
      if (!Number.isInteger(pendingTargetSlideIndex)) {
        pendingTabFocusTarget = null
        return
      }

      const targetSlide = carousel.slides[pendingTargetSlideIndex]
      const firstFocusable = targetSlide?.querySelector(focusableSelector)

      pendingTabFocusTarget = firstFocusable || null

      if (pendingTabIntent && pendingTabFocusTarget) {
        pendingTabFocusTarget.focus({ preventScroll: true })
        pendingTabFocusTarget = null
        pendingTabIntent = false
      }
    }

    const resolvePendingTarget = () => {
      if (!pendingFocusMove) {
        return
      }

      pendingFocusMove = false
      resolveTargetFocusable()
    }

    carousel.on('animationEnded', resolvePendingTarget)

    const onArrowActivate = (direction, event) => {
      const wasKeyboardActivation =
        isKeyboardArrowActivation || event?.detail === 0

      isKeyboardArrowActivation = false

      if (!wasKeyboardActivation) {
        pendingFocusMove = false
        pendingTargetSlideIndex = null
        pendingTabFocusTarget = null
        pendingTabIntent = false
        return
      }

      pendingTargetSlideIndex = getNavigationTarget(
        carousel,
        direction,
        isLarge && pagedViewport.matches,
      )
      pendingFocusMove = Number.isInteger(pendingTargetSlideIndex)
    }

    const onArrowKeydown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        isKeyboardArrowActivation = true
        return
      }

      if (event.key !== 'Tab' || event.shiftKey) {
        return
      }

      if (!pendingTabFocusTarget && !pendingFocusMove) {
        return
      }

      event.preventDefault()

      if (pendingTabFocusTarget) {
        pendingTabFocusTarget.focus({ preventScroll: true })
        pendingTabFocusTarget = null
        pendingTabIntent = false
        return
      }

      pendingTabIntent = true
    }

    this.els?.next?.element?.addEventListener(
      'click',
      (event) => {
        onArrowActivate('next', event)
      },
      true,
    )
    this.els?.previous?.element?.addEventListener(
      'click',
      (event) => {
        onArrowActivate('previous', event)
      },
      true,
    )

    this.els?.next?.element?.addEventListener('keydown', onArrowKeydown)
    this.els?.previous?.element?.addEventListener('keydown', onArrowKeydown)
  }
}

if (!customElements.get('cta-carousel')) {
  customElements.define('cta-carousel', CtaCarousel)
}
